import { type ErrorInfo, type ReactNode } from "react";
import { ErrorBoundary, type FallbackProps } from "react-error-boundary";

import Button from "./Button";
import { attemptChunkReload } from "./chunk-error";
import { reportUiError } from "./error-reporting";
import { m } from "./paraglide/messages";

export interface AppBoundaryFallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

interface AppErrorBoundaryProps {
  children: ReactNode;
  area: string;
  description?: string;
  fallback?: (props: AppBoundaryFallbackProps) => ReactNode;
  resetKeys?: ReadonlyArray<unknown>;
  onReset?: () => void;
}

interface RootErrorFallbackProps {
  resetErrorBoundary: () => void;
}

interface FeatureErrorFallbackProps extends Readonly<RootErrorFallbackProps> {
  description: string;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) {
    return error;
  }

  return new Error(String(error));
}

export function RootErrorFallback({
  resetErrorBoundary,
}: Readonly<RootErrorFallbackProps>) {
  return (
    <main className="flex min-h-screen items-center justify-center bg-white px-6 py-12">
      <div className="w-full max-w-md space-y-4 text-center">
        <h1 className="text-xl font-semibold text-zinc-950">
          {m.error_occurred()}
        </h1>
        <p className="text-sm text-zinc-600">{m.root_render_error_message()}</p>
        <div className="flex justify-center gap-3">
          <button
            type="button"
            className="rounded-md border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-900"
            onClick={resetErrorBoundary}
          >
            {m.try_again_button()}
          </button>
          <button
            type="button"
            className="rounded-md bg-zinc-950 px-4 py-2 text-sm font-medium text-white"
            onClick={() => globalThis.location.reload()}
          >
            {m.refresh_page()}
          </button>
        </div>
      </div>
    </main>
  );
}

export function FeatureErrorFallback({
  description,
  resetErrorBoundary,
}: Readonly<FeatureErrorFallbackProps>) {
  return (
    <div className="bg-background-2 border-border flex min-h-48 w-full flex-col items-center justify-center gap-3 rounded-lg border px-6 py-8 text-center">
      <h2 className="text-text-primary text-base font-semibold">
        {m.error_occurred()}
      </h2>
      <p className="text-text-secondary max-w-lg text-sm">{description}</p>
      <Button onPress={resetErrorBoundary}>{m.try_again_button()}</Button>
    </div>
  );
}

export function AppErrorBoundary({
  children,
  area,
  description,
  fallback,
  resetKeys,
  onReset,
}: Readonly<AppErrorBoundaryProps>) {
  function handleError(error: unknown, info: ErrorInfo) {
    const normalizedError = normalizeError(error);

    // stale hashed chunk after a deploy: recover with a reload instead of
    // stranding the user on the fallback UI
    attemptChunkReload(normalizedError);

    reportUiError(normalizedError, {
      area,
      errorBoundary: "true",
      pathname: globalThis.location.pathname,
      source: "react",
      ...(info.componentStack ? { componentStack: info.componentStack } : {}),
    });
  }

  function renderFallback({ error, resetErrorBoundary }: FallbackProps) {
    if (fallback) {
      return fallback({ error: normalizeError(error), resetErrorBoundary });
    }

    return (
      <FeatureErrorFallback
        resetErrorBoundary={resetErrorBoundary}
        description={description ?? m.feature_render_error_message()}
      />
    );
  }

  return (
    <ErrorBoundary
      onError={handleError}
      onReset={(details) => {
        if (details.reason === "imperative-api") {
          onReset?.();
        }
      }}
      resetKeys={resetKeys ? [...resetKeys] : undefined}
      fallbackRender={renderFallback}
    >
      {children}
    </ErrorBoundary>
  );
}
